import 'url-polyfill';
import { merge, pipe, tap } from 'ramda';
import { init, jumpToEnd } from './screenStreams';
import {
  Config,
  LoadingErrorResponse,
  LoadingResponse,
  LoadingStandardResponse,
  SurveyParams,
  Values,
} from './SurveyCollector';
import {
  displayConnectionError,
  getLastTransition,
  goToFillInBlocked,
  goToLoading,
  StateType,
} from '../uiState/states';
import { getFirstBrowserLanguage } from './languageDetection';
import { mergeDefinitionAndTranslations } from 'skoda-xperience-model-management';

import { cleanSurveyData, loadSurveyData } from './localStore';
import { sendData } from './surveySubmitting';
import {
  acquireParamsFromUrl,
  createSurveyUrl,
  StartQuestionParams,
  UrlSurveyParams,
} from './surveyUrl';
import { surveyPreProcessing } from '../surveyModel/preProcessing';
import {
  createSurveyMetadata,
  getCommonTranslations,
} from '../surveyModel/getters';

const processLoadedSurvey = (
  config: Config,
  surveyParams: SurveyParams,
  startQuestionParams: StartQuestionParams
) => (response: LoadingResponse): void => {
  let reason;

  switch (response.surveyStatus) {
    case 'NEW':
    case 'IN_PROGRESS': {
      const processedSurvey = surveyPreProcessing(response.survey);

      const surveyForMerge =
        normalizeSurveyBeforeTranslationMerge(processedSurvey);

      const mergedDefinition = mergeDefinitionAndTranslations(surveyForMerge);

      const startQuestionAnswer = startQuestionParams.startQuestionAnswer;
      let hasStartQuestionAnswer = false;
      let collectedDataWithStartQuestionAnswer = response.collectedData || {};

      if (!isEmptyValue(startQuestionAnswer)) {
        hasStartQuestionAnswer = true;
        collectedDataWithStartQuestionAnswer =
          applyStartQuestionAnswerFromUrl(
            mergedDefinition,
            collectedDataWithStartQuestionAnswer,
            startQuestionAnswer
          );
      }

      init(
        config,
        surveyParams,
        mergedDefinition,
        response.sampleData,
        collectedDataWithStartQuestionAnswer,
        response.events || [],
        getCommonTranslations(surveyForMerge),
        createSurveyMetadata(response, surveyForMerge),
        response.brand,
        {
          processInitialTemporaryData: !hasStartQuestionAnswer,
          suppressInitialAutoNext: hasStartQuestionAnswer,
        }
      );
      break;
    }

    case 'FINISHED':
      reason = response.userMessage || 'Survey finished.';
      goToFillInBlocked(reason);
      break;

    case 'EXPIRED':
      reason = response.userMessage || 'Survey expired.';
      goToFillInBlocked(reason);
      break;

    case 'OPT_OUT':
      reason = response.userMessage || 'Survey opt out.';
      goToFillInBlocked(reason);
      break;

    default:
      goToFillInBlocked('Unknown error');
      break;
  }
};

const normalizeSurveyBeforeTranslationMerge = (survey: any): any => {
  if (!survey?.translations || !Array.isArray(survey?.definition)) {
    return survey;
  }

  const definitionElements = collectDefinitionElements(survey.definition);
  const definitionIds = definitionElements.map((element) => element.id);

  const normalizedTranslations = Object.keys(survey.translations).reduce(
    (result: any, translationKey: string) => {
      const translationValue = survey.translations[translationKey];

      if (translationKey === 'common') {
        return {
          ...result,
          [translationKey]: translationValue,
        };
      }

      if (!definitionIds.includes(translationKey)) {
        return result;
      }

      if (
        translationValue === undefined ||
        translationValue === null ||
        typeof translationValue !== 'object' ||
        Array.isArray(translationValue)
      ) {
        return result;
      }

      return {
        ...result,
        [translationKey]: translationValue,
      };
    },
    {}
  );

  return {
    ...survey,
    translations: normalizedTranslations,
  };
};

const collectDefinitionElements = (elements: any): any[] => {
  if (!Array.isArray(elements)) {
    return [];
  }

  return elements.reduce((result: any[], element: any) => {
    const currentElement =
      element?.id && element?.type
        ? [
          {
            id: element.id,
            type: element.type,
          },
        ]
        : [];

    const nestedElements = Array.isArray(element?.elements)
      ? collectDefinitionElements(element.elements)
      : [];

    return [...result, ...currentElement, ...nestedElements];
  }, []);
};

const applyStartQuestionAnswerFromUrl = (
  surveyDefinition: any,
  collectedData: Values,
  startQuestionAnswer: string
): Values => {
  const firstDefinitionItem = surveyDefinition[0];

  const firstQuestion =
    firstDefinitionItem?.type === 'page'
      ? firstDefinitionItem.elements?.[0]
      : firstDefinitionItem;

  if (!firstQuestion?.id) {
    return collectedData;
  }

  if (!isEmptyValue(collectedData[firstQuestion.id])) {
    return collectedData;
  }

  return {
    ...collectedData,
    [firstQuestion.id]: normalizeStartQuestionAnswer(
      firstQuestion,
      startQuestionAnswer
    ),
  };
};

const normalizeStartQuestionAnswer = (
  question: any,
  answerValue: string
): string | number => {
  const matchingChoice = question.choices?.find(
    (choice) => String(choice.value) === String(answerValue)
  );

  if (matchingChoice) {
    return matchingChoice.value;
  }

  if (isRatingQuestion(question.type)) {
    const numericAnswerValue = Number(answerValue);

    if (!Number.isNaN(numericAnswerValue)) {
      return numericAnswerValue;
    }
  }

  return answerValue;
};

const isRatingQuestion = (questionType: string): boolean =>
  [
    'questionStarRating',
    'questionSquareRating',
    'questionStarRanking',
  ].includes(questionType);

const isEmptyValue = (value: any): value is undefined | null | '' =>
  value === undefined || value === null || value === '';

const processLoadingError = (response: LoadingErrorResponse): void => {
  console.error(response);

  goToFillInBlocked('Survey loading failed', response.status);
};

const loadSurveyAsynchronously = (config: Config) => ({
                                                        surveyParams,
                                                        searchParams,
                                                        startQuestionParams,
                                                      }: UrlSurveyParams) => {
  fetch(createSurveyUrl(config.REACT_APP_API_URI, surveyParams, searchParams), {
    method: 'GET',
    headers: {
      'Accept-Language': getFirstBrowserLanguage(),
      Accept: 'application/json',
    },
  })
    .then((response) => {
      if (response.ok) {
        return response;
      }

      throw response;
    })
    .then((response) => response.json())
    .then(mergeWithSavedData(config, surveyParams))
    .then(processLoadedSurvey(config, surveyParams, startQuestionParams))
    .catch(processLoadingError);
};

export const loadSurveyByLocation = (config: Config, currentUrl: string) => {
  // TODO better error processing
  try {
    return pipe<string, string, UrlSurveyParams, void>(
      tap(() => goToLoading()),
      acquireParamsFromUrl,
      loadSurveyAsynchronously(config)
    )(currentUrl);
  } catch (e) {
    console.error(e);
    goToFillInBlocked('Unknown error');
  }
};

export const mergeWithSavedData = (
  config: Config,
  surveyParams: SurveyParams
) => (response: LoadingStandardResponse) => {
  const savedSurveyData = loadSurveyData(surveyParams);
  const surveyMetaData = createSurveyMetadata(response, savedSurveyData);

  if (savedSurveyData.surveyStatus === 'FINISHED') {
    sendData(config, surveyParams, surveyMetaData, savedSurveyData)
      .then(() => {
        const lastTransition = getLastTransition();

        if (
          savedSurveyData.surveyStatus === 'FINISHED' &&
          lastTransition.type === StateType.ConnectionErrorDisplayed
        ) {
          jumpToEnd('offlineRecovery');
        }
      })
      .catch(() => {
        if (savedSurveyData.surveyStatus === 'FINISHED') {
          cleanSurveyData(surveyParams);
          displayConnectionError();
        }
      });
  }

  return merge(response, savedSurveyData) as LoadingStandardResponse;
};
